import React from "react";
import { Link } from "react-router-dom";
import { Check, Lock, ArrowRight } from "lucide-react";
import { Panel } from "./ui";
import { ProgressBar } from "./ui-extras";
import { useTranslation } from "react-i18next";

/**
 * Account setup progress.
 *
 * Ordered so the next incomplete step is the most valuable one available, and the
 * whole card disappears once finished — a permanent checklist that can never reach
 * 100% is nagging, not onboarding.
 */
export default function CompletionChecklist({ completion, compact = false }) {
  const { t } = useTranslation();
  if (!completion || completion.complete) return null;

  const steps = compact ? completion.steps.filter((s) => !s.done).slice(0, 3) : completion.steps;

  return (
    <Panel className="card p-5" testid="completion-checklist">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold tracking-tight">{t("completion.title")}</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {t("completion.progress", { done: completion.done, total: completion.total })}
          </p>
        </div>
        <span className="text-2xl font-heading font-semibold tabular text-brand-600">{completion.pct}%</span>
      </div>
      <ProgressBar pct={completion.pct} className="mt-3" />

      <div className="mt-4 space-y-1">
        {steps.map((s) => (
          <Link
            key={s.key}
            to={s.locked ? "#" : s.href}
            data-testid={`step-${s.key}`}
            onClick={(e) => s.locked && e.preventDefault()}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
              s.locked ? "opacity-60 cursor-default" : "hover:bg-slate-50"
            }`}
          >
            <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 ${
              s.done ? "bg-emerald-500 text-white"
              : s.locked ? "bg-slate-100 text-slate-400"
              : "border-2 border-slate-200"
            }`}>
              {s.done ? <Check size={12} strokeWidth={3} /> : s.locked ? <Lock size={10} /> : null}
            </span>
            <span className={`text-sm flex-1 min-w-0 ${s.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
              {t(`completion.step.${s.key}`)}
              {s.required && !s.done && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                  {t("completion.required")}
                </span>
              )}
              {s.locked && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {t("completion.comingSoon")}
                </span>
              )}
            </span>
            {!s.done && !s.locked && <ArrowRight size={14} className="text-slate-300 shrink-0" />}
          </Link>
        ))}
      </div>
    </Panel>
  );
}
